import { useEffect, useMemo, useState } from "react";
import {
  addDoc, collection, deleteDoc, doc, getDocs, query, where,
} from "firebase/firestore";

import { db } from "../../firebase";
import { useAuth } from "../../context/AuthContext";
import { isFoundingCreator } from "../../services/dropPublishService";
import CreatorStudioLayout from "./CreatorStudioLayout";

const STROKE = 1.8;
const MAX_TIERS = 3;
const PRICE_PRESETS = ["$4.99", "$9.99", "$14.99", "$19.99"];

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PerkLine({ children }) {
  return (
    <div className="cp-tcp">
      <CheckIcon />
      {children}
    </div>
  );
}

function ToggleRow({ title, desc, value, onChange }) {
  return (
    <div className="cp-toggle-row">
      <div>
        <div className="cp-tr-title">{title}</div>
        <div className="cp-tr-desc">{desc}</div>
      </div>
      <button type="button" className={`cp-toggle${value ? " on" : ""}`} onClick={onChange} aria-pressed={value} />
    </div>
  );
}

function tierPerks(tier) {
  const perks = [];
  if (tier.allowEarlyDrops) perks.push("Early window access");
  if (tier.allowUnlocks) perks.push("Unlock credits included");
  if (tier.priorityReplies) perks.push("Priority replies");
  if (tier.description) perks.push(tier.description);
  if (!perks.length) perks.push("Exclusive supporter perks");
  return perks;
}

export default function SubscriptionTiers() {
  const { currentUser, userProfile } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [name, setName] = useState("");
  const [price, setPrice] = useState("$9.99");
  const [description, setDescription] = useState("");
  const [allowEarlyDrops, setAllowEarlyDrops] = useState(true);
  const [allowUnlocks, setAllowUnlocks] = useState(true);
  const [priorityReplies, setPriorityReplies] = useState(false);

  const founding = isFoundingCreator(userProfile);

  const foundingSubCount = useMemo(
    () => tiers.reduce((sum, t) => sum + (Number(t.subscriberCount) || 0), 0),
    [tiers]
  );

  const showToast = (text, type = "success") => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const resetForm = () => {
    setName("");
    setPrice("$9.99");
    setDescription("");
    setAllowEarlyDrops(true);
    setAllowUnlocks(true);
    setPriorityReplies(false);
    setShowForm(false);
  };

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    getDocs(query(collection(db, "supportTiers"), where("creatorId", "==", currentUser.uid)))
      .then((snap) => setTiers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((err) => console.error("Load tiers error:", err))
      .finally(() => setLoading(false));
  }, [currentUser?.uid]);

  const handleCreate = async () => {
    if (!currentUser) return;
    if (!name.trim()) {
      showToast("Tier name is required.", "error");
      return;
    }
    if (tiers.length >= MAX_TIERS) {
      showToast(`Maximum ${MAX_TIERS} paid tiers allowed.`, "error");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        creatorId: currentUser.uid,
        name: name.trim(),
        price: price.trim(),
        description: description.trim(),
        allowEarlyDrops,
        allowUnlocks,
        priorityReplies,
        subscriberCount: 0,
        createdAt: new Date(),
      };
      const ref = await addDoc(collection(db, "supportTiers"), payload);
      setTiers((prev) => [{ id: ref.id, ...payload }, ...prev]);
      resetForm();
      showToast("Subscription tier created!");
    } catch (err) {
      console.error("Create tier error:", err);
      showToast("Could not create tier.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId) => {
    try {
      await deleteDoc(doc(db, "supportTiers", tierId));
      setTiers((prev) => prev.filter((t) => t.id !== tierId));
      setDeleteId(null);
      showToast("Tier deleted.");
    } catch {
      showToast("Could not delete tier.", "error");
    }
  };

  return (
    <CreatorStudioLayout activePath="/support-tiers">
      {toast && (
        <div className={`cp-toast cp-toast-${toast.type === "error" ? "error" : "success"}`}>
          {toast.text}
        </div>
      )}

      {deleteId && (
        <div className="cs-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setDeleteId(null)}>
          <div className="cs-card" style={{ maxWidth: 360, padding: 24 }} onClick={(e) => e.stopPropagation()}>
            <strong style={{ display: "block", marginBottom: 8 }}>Delete this tier?</strong>
            <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>Supporters on this tier will lose access. This cannot be undone.</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button type="button" className="cp-btn cp-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" className="cp-btn cp-btn-danger" onClick={() => handleDelete(deleteId)}>Delete tier</button>
            </div>
          </div>
        </div>
      )}

      <div className="cp-page-header">
        <div>
          <div className="cs-page-eyebrow">Monetisation</div>
          <h1 className="cp-section-title">Subscription <em>Tiers</em></h1>
          <span className="cp-section-hand">how fans access your content on a recurring basis</span>
        </div>
        {!showForm && tiers.length < MAX_TIERS && (
          <button type="button" className="cp-btn cp-btn-gold" onClick={() => setShowForm(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add tier
          </button>
        )}
      </div>

      {loading ? (
        <div className="cs-loading">Loading tiers…</div>
      ) : (
        <div className="cp-tier-grid">
          <div className="cp-tier-card active-tier">
            <div className="cp-tc-header">
              <div className="cp-tc-name-row">
                <span className="cp-tc-name">Follower</span>
                <span className="cp-tc-badge cp-tcb-active">Active</span>
              </div>
              <div className="cp-tc-price">Free</div>
              <div className="cp-tc-sub">Follow only — no subscription required</div>
            </div>
            <div className="cp-tc-divider" />
            <div className="cp-tc-perks">
              <PerkLine>Early follower window access</PerkLine>
              <PerkLine>Buy individual drops at list price</PerkLine>
              <PerkLine>Earn credits on every unlock</PerkLine>
            </div>
          </div>

          {tiers.map((tier) => (
            <div key={tier.id} className="cp-tier-card active-tier">
              <div className="cp-tc-header">
                <div className="cp-tc-name-row">
                  <span className="cp-tc-name">{tier.name}</span>
                  <span className="cp-tc-badge cp-tcb-active">Active</span>
                </div>
                <div className="cp-tc-price">
                  {tier.price}<span>/mo</span>
                </div>
                {founding && (
                  <div className="cp-tc-founding-note">✦ Founding rate — locked for early subscribers</div>
                )}
              </div>
              <div className="cp-tc-divider" />
              <div className="cp-tc-perks">
                {tierPerks(tier).map((perk) => (
                  <PerkLine key={perk}>{perk}</PerkLine>
                ))}
                {(tier.subscriberCount || 0) > 0 && (
                  <PerkLine>{tier.subscriberCount} active subscribers</PerkLine>
                )}
              </div>
              <div className="cp-tc-footer">
                <button type="button" className="cp-btn cp-btn-ghost" style={{ flex: 1, justifyContent: "center", fontSize: 11 }} disabled>
                  Edit perks
                </button>
                <button type="button" className="cp-btn cp-btn-danger" style={{ fontSize: 11 }} onClick={() => setDeleteId(tier.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}

          {tiers.length < MAX_TIERS && !showForm && (
            <button type="button" className="cp-add-tier-card" onClick={() => setShowForm(true)}>
              <div className="cp-atc-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              </div>
              <div className="cp-atc-label">Add Premium Tier</div>
              <div className="cp-atc-sub">A higher tier with exclusive content or perks — e.g. $19/mo with bonus access</div>
            </button>
          )}
        </div>
      )}

      {showForm && (
        <div className="cp-drop-form">
          <div className="cp-df-section">
            <div className="cp-df-section-title">
              <span className="cp-df-section-num">1</span>
              Tier details
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Tier name</label>
              <input className="cp-field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Supporter Circle, Inner Circle…" maxLength={40} />
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Monthly price (CAD)</label>
              <div className="cp-price-presets">
                {PRICE_PRESETS.map((p) => (
                  <button key={p} type="button" className={`cp-price-preset${price === p ? " active" : ""}`} onClick={() => setPrice(p)}>
                    {p}
                  </button>
                ))}
              </div>
              <input className="cp-field-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Custom price e.g. $7.99" />
              <span className="cp-field-hint">You keep 90% as a founding creator (10% platform fee)</span>
            </div>
            <div className="cp-field">
              <label className="cp-field-label">Description</label>
              <textarea className="cp-field-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tell fans what they get — be specific about exclusive perks…" maxLength={200} rows={3} />
            </div>
          </div>

          <div className="cp-df-section">
            <div className="cp-df-section-title">
              <span className="cp-df-section-num">2</span>
              Supporter perks
            </div>
            <ToggleRow title="Early drop access" desc="Supporters see your drops before the public" value={allowEarlyDrops} onChange={() => setAllowEarlyDrops((p) => !p)} />
            <ToggleRow title="Unlock credits" desc="Allow supporters to use perk credits on your limited moments" value={allowUnlocks} onChange={() => setAllowUnlocks((p) => !p)} />
            <ToggleRow title="Priority replies" desc="Supporter comments are highlighted and seen first" value={priorityReplies} onChange={() => setPriorityReplies((p) => !p)} />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button type="button" className="cp-btn cp-btn-ghost" onClick={resetForm}>Cancel</button>
            <button type="button" className="cp-btn cp-btn-gold" onClick={handleCreate} disabled={saving}>
              {saving ? "Creating…" : "Create tier"}
            </button>
          </div>
        </div>
      )}

      <div className="cs-card" style={{ marginTop: 24 }}>
        <div className="cs-card-head">
          <div>
            <div className="cs-card-title">Founding Subscriber Rate</div>
            <div className="cs-card-sub">Subscribers who join before you hit 1,000 subscribers lock in your current price forever</div>
          </div>
          <span style={{ fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 100, background: "var(--gold-light)", color: "var(--gold)", border: "1px solid rgba(196,121,10,0.2)" }}>
            Active ✓
          </span>
        </div>
        <div style={{ fontSize: 13, color: "var(--text-mid)", lineHeight: 1.7 }}>
          Currently <strong>{foundingSubCount} founding subscribers</strong> across your paid tiers.
          {tiers[0] ? ` If you raise your price in future, they never pay more than ${tiers[0].price}/mo.` : " Create your first paid tier to start building founding subscribers."}
          {" "}This is your commitment to your earliest supporters.
        </div>
      </div>
    </CreatorStudioLayout>
  );
}
